import type { Diagnostics, DiagnosticStep } from "@/api/queries";
import { StatusDot, type Tone } from "@/components/ui/Chip";
import { ShieldIcon } from "@/components/icons";

const TONES: Record<string, Tone> = {
  ok: "success",
  warning: "warning",
  failed: "error",
  skipped: "neutral",
};

function Step({ step }: { step: DiagnosticStep }) {
  return (
    <li className="flex items-start gap-3 py-2.5">
      <StatusDot tone={TONES[step.status] ?? "neutral"} className="mt-1.5" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="type-label text-ink">{step.label}</span>
          {step.duration_ms !== null && step.duration_ms !== undefined ? (
            <span className="type-caption text-ink-disabled tabular-nums">
              {step.duration_ms} ms
            </span>
          ) : null}
        </div>
        {step.detail ? (
          <p className="type-support text-ink-secondary mt-0.5">{step.detail}</p>
        ) : null}
      </div>
    </li>
  );
}

/**
 * The Test Connection ladder (PRD FR-1.2). Every rung is a real check against
 * the server, including how the read-only session is enforced — that is the
 * claim an administrator most needs to see evidence for.
 */
export function DiagnosticsReport({ report }: { report: Diagnostics }) {
  return (
    <div className="rounded-container border border-border bg-card">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border px-4 py-3">
        <StatusDot tone={report.ok ? "success" : "error"} />
        <span className="type-h4 text-ink">
          {report.ok ? "Connection verified" : "Connection failed"}
        </span>
        {report.server_version ? (
          <span className="type-support text-ink-secondary">{report.server_version}</span>
        ) : null}
        {report.relation_count !== null && report.relation_count !== undefined ? (
          <span className="type-support text-ink-secondary tabular-nums">
            · {report.relation_count} relations
          </span>
        ) : null}
      </header>
      <ul className="divide-y divide-border px-4 py-1">
        {report.steps.map((step) => (
          <Step key={step.key} step={step} />
        ))}
      </ul>
      {report.read_only_enforcement ? (
        <footer className="flex items-start gap-2 border-t border-border bg-surface px-4 py-3">
          <span className="text-ink-disabled mt-0.5">
            <ShieldIcon className="size-4" />
          </span>
          <p className="type-support text-ink-secondary">
            Read-only enforcement: {report.read_only_enforcement}
          </p>
        </footer>
      ) : null}
    </div>
  );
}
